import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL
  || 'http://localhost:3001';

// ── Constants ───────────────────────────────────────────────────
const INTENT_COLORS = {
  transactional: { bg: '#dcfce7', color: '#166534' },
  informational: { bg: '#eff6ff', color: '#1e40af' },
  commercial:    { bg: '#fef9c3', color: '#854d0e' },
  navigational:  { bg: '#f3f4f6', color: '#374151' },
};

const PRIORITY_COLORS = {
  high:   { bg: '#fef2f2', color: '#991b1b' },
  medium: { bg: '#fef9c3', color: '#854d0e' },
  low:    { bg: '#f3f4f6', color: '#6b7280' },
};

const BACKLINK_STATUS_COLORS = {
  linked:      { bg: '#dcfce7', color: '#166534', label: 'Linked' },
  pitched:     { bg: '#eff6ff', color: '#1e40af', label: 'Pitched' },
  pending:     { bg: '#fef9c3', color: '#854d0e', label: 'In Progress' },
  identified:  { bg: '#f3f4f6', color: '#374151', label: 'Identified' },
  declined:    { bg: '#fef2f2', color: '#991b1b', label: 'Declined' },
};

const REC_TYPE_COLORS = {
  content_gap:        { bg: '#f3e8ff', color: '#6b21a8', label: 'Content Gap' },
  page_optimization:  { bg: '#eff6ff', color: '#1e40af', label: 'Page Optimization' },
  backlink_target:    { bg: '#dcfce7', color: '#166534', label: 'Backlink Target' },
  keyword_opportunity:{ bg: '#fef9c3', color: '#854d0e', label: 'Keyword Opportunity' },
  technical_seo:      { bg: '#fce7f3', color: '#9d174d', label: 'Technical SEO' },
  competitor_gap:     { bg: '#fef2f2', color: '#991b1b', label: 'Competitor Gap' },
};

function fmt(num, decimals = 0) {
  if (num === null || num === undefined) return '—';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function DeltaBadge({ delta, lowerIsBetter = false }) {
  if (delta === null || delta === undefined) return null;
  // 'isPositive' = "is this an improvement?", NOT raw sign. For avg
  // position lower is better (rank 3 -> 1 is an improvement), so the
  // sign-to-improvement mapping is inverted — driven by lowerIsBetter
  // at the call site. Color always follows improvement direction; raw
  // arrow direction follows the literal sign so a green ↓ on Avg
  // Position reads correctly ("rank dropped to a better number").
  const isPositive = lowerIsBetter ? delta < 0 : delta > 0;
  const isFlat = Math.abs(delta) <= 3;
  // Doorly brand red on losses; readable green on improvements. The
  // brand lime (#acf44e) is illegible as small text on the dark hero
  // bg used here AND as small text on white — we keep it for the badge
  // pill on PositionBadge (background fill) but never as text color.
  const color = isFlat ? '#9ca3af'
    : isPositive ? '#16a34a' : '#ff082f';
  const arrow = isFlat ? '→' : delta > 0 ? '↑' : '↓';
  return (
    <div style={{ fontSize: 12, color, fontWeight: 500, marginTop: 2 }}>
      {arrow}{Math.abs(delta)}% vs prior period
    </div>
  );
}

// Classification badge — pairs with the seoClassify.js status enum on
// the backend. Color spec from product:
//   live            → green pill (#acf44e bg, #111827 text)
//   live_no_traffic → blue pill, exists-but-not-ranking
//   recommended     → amber pill (#ffdd86 bg, #111827 text)
//   broken          → red pill (#ff082f bg, white text)
//   unknown         → gray pill, Webflow unavailable
//   untargeted      → gray outline, no target set
// The badge surfaces `classification.reason` via a native title tooltip
// so the strategist can hover any row to read the rationale.
const CLASSIFICATION_CFG = {
  live:            { bg: '#acf44e', color: '#111827', label: 'Live',         border: '#acf44e' },
  live_no_traffic: { bg: '#dbeafe', color: '#1e40af', label: 'No traffic',   border: '#bfdbfe' },
  recommended:     { bg: '#ffdd86', color: '#111827', label: 'Recommended',  border: '#ffdd86' },
  broken:          { bg: '#ff082f', color: '#ffffff', label: 'Broken',       border: '#ff082f' },
  unknown:         { bg: '#f3f4f6', color: '#6b7280', label: 'Unknown',      border: '#e5e7eb' },
  untargeted:      { bg: '#ffffff', color: '#9ca3af', label: 'Untargeted',   border: '#e5e7eb' },
};

function ClassificationBadge({ classification }) {
  if (!classification) return null;
  const cfg = CLASSIFICATION_CFG[classification.status] || CLASSIFICATION_CFG.unknown;
  return (
    <span
      title={classification.reason || ''}
      style={{
        display: 'inline-block',
        background: cfg.bg, color: cfg.color,
        border: `1px solid ${cfg.border}`,
        fontSize: 11, fontWeight: 700,
        borderRadius: 6, padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}>
      {cfg.label}
    </span>
  );
}

function PositionBadge({ position }) {
  if (!position) return <span style={{ color: '#9ca3af', fontSize: 12 }}>Not ranking</span>;
  const pos = Math.round(position * 10) / 10;
  const color = pos <= 3 ? '#166534'
    : pos <= 10 ? '#1e40af'
    : pos <= 20 ? '#854d0e' : '#6b7280';
  const bg = pos <= 3 ? '#dcfce7'
    : pos <= 10 ? '#eff6ff'
    : pos <= 20 ? '#fef9c3' : '#f3f4f6';
  return (
    <span style={{
      background: bg, color, fontSize: 11,
      fontWeight: 700, borderRadius: 6,
      padding: '2px 8px',
    }}>
      #{pos}
    </span>
  );
}

// ── Crawl Audit panel + verdict badge ─────────────────────────────────
// Verdict color map mirrors the seoAudit.js status set:
//   blocked         → red (must fix — noindex / 4xx / robots-disallow)
//   canonical_issue → red (must fix — canonical points elsewhere)
//   not_in_sitemap  → amber (cheap fix — add to /sitemap.xml)
//   unreachable     → muted gray (network / timeout — retry)
//   healthy_pending → neutral green (no on-page problem found —
//                     honest framing: needs time or stronger content,
//                     NOT a 'fix this bug' signal)
const VERDICT_CFG = {
  blocked:          { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'Blocked' },
  canonical_issue:  { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'Canonical issue' },
  not_in_sitemap:   { bg: '#fffbeb', color: '#92400e', border: '#fde68a', label: 'Not in sitemap' },
  unreachable:      { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb', label: 'Unreachable' },
  healthy_pending:  { bg: '#f0fdf4', color: '#166534', border: '#86efac', label: 'Healthy · pending' },
};

function VerdictBadge({ verdict }) {
  const cfg = VERDICT_CFG[verdict] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb', label: verdict || '—' };
  return (
    <span style={{
      display: 'inline-block',
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      fontSize: 11, fontWeight: 700,
      borderRadius: 6, padding: '2px 8px',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function CrawlAuditPanel({ result, error, onDismiss }) {
  if (error) {
    return (
      <div style={{
        background: '#fef2f2', border: '1px solid #fecaca',
        borderLeft: '4px solid #ef4444', borderRadius: 8,
        padding: '12px 14px', marginBottom: 16,
        fontSize: 12, color: '#991b1b',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Crawl audit failed</div>
            <div>{error}</div>
          </div>
          <button onClick={onDismiss}
            style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontWeight: 700 }}>
            ✕
          </button>
        </div>
      </div>
    );
  }
  if (!result) return null;
  const { audited, summary, sitemapAvailable, robotsAvailable, results, message, ranAt } = result;
  if (audited === 0) {
    return (
      <div style={{
        background: '#f0fdf4', border: '1px solid #86efac',
        borderLeft: '4px solid #16a34a', borderRadius: 8,
        padding: '12px 14px', marginBottom: 16,
        fontSize: 12, color: '#166534',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Nothing to crawl</div>
            <div>{message || 'No keywords currently classify as live_no_traffic.'}</div>
          </div>
          <button onClick={onDismiss}
            style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontWeight: 700 }}>
            ✕
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb',
      borderRadius: 12, padding: 16, marginBottom: 16,
    }}>
      {/* Header — summary counts + ran-at + dismiss */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
            Crawl Audit · {audited} URL{audited === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            Scoped to keywords classified <span style={{ background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>live_no_traffic</span>
            {ranAt && <> · ran {new Date(ranAt).toLocaleTimeString()}</>}
            {sitemapAvailable === false && <span style={{ marginLeft: 8, color: '#92400e' }}>· ⚠ sitemap.xml unreachable</span>}
            {robotsAvailable === false && <span style={{ marginLeft: 8, color: '#92400e' }}>· ⚠ robots.txt unreachable</span>}
          </div>
        </div>
        <button onClick={onDismiss}
          style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
          title="Dismiss audit results">
          ✕
        </button>
      </div>

      {/* Summary counts */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {['blocked', 'canonical_issue', 'not_in_sitemap', 'unreachable', 'healthy_pending'].map((k) => {
          const n = summary?.[k] || 0;
          if (n === 0) return null;
          return (
            <span key={k} style={{
              fontSize: 11, fontWeight: 600,
              padding: '3px 9px', borderRadius: 999,
              background: VERDICT_CFG[k].bg, color: VERDICT_CFG[k].color,
              border: `1px solid ${VERDICT_CFG[k].border}`,
            }}>
              {n} {VERDICT_CFG[k].label}
            </span>
          );
        })}
      </div>

      {/* Per-URL results */}
      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
        {results.map((r, i) => (
          <div key={r.url + ':' + i} style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr',
            gap: 12,
            padding: '10px 0',
            borderTop: i === 0 ? 'none' : '1px solid #f9fafb',
          }}>
            <div>
              <VerdictBadge verdict={r.verdict} />
              {r.status != null && (
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, fontFamily: 'monospace' }}>
                  HTTP {r.status}{r.redirected ? ' · redirected' : ''}
                </div>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', wordBreak: 'break-all' }}>
                {r.url}
              </div>
              {r.keywords?.length > 0 && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  Keywords: {r.keywords.map(k => k.keyword).join(' · ')}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#374151', marginTop: 4, lineHeight: 1.5 }}>
                {r.reason}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Honest framing on the verdict color palette */}
      <div style={{
        borderTop: '1px solid #f3f4f6', marginTop: 10, paddingTop: 10,
        fontSize: 10, color: '#9ca3af', lineHeight: 1.6,
      }}>
        Verdict meanings: <strong>Blocked</strong> / <strong>Canonical issue</strong> = real on-page problem, must fix. <strong>Not in sitemap</strong> = cheap one-flip fix. <strong>Unreachable</strong> = retry (network / timeout). <strong>Healthy · pending</strong> = no indexability problem found — page may simply need more time or stronger content/links to earn impressions. Not a bug.
      </div>
    </div>
  );
}

// ── Brand Scoreboard panel ────────────────────────────────────────────
// Per branded term, renders the owned slots + the non-owned slots
// (collapsed by default). Top of panel shows coverage summary (N of 9
// owned properties appear in ANY branded SERP) + which properties never
// surfaced. Refresh button busts the backend's 24h serper cache.
function ScoreboardPanel({ data, loading, error, onRefresh }) {
  // Lazy-load on first tab visit. The parent passes a stable onRefresh;
  // we only fire it when data + error are both null AND we're not
  // already loading.
  React.useEffect(() => {
    if (!data && !error && !loading) onRefresh(false);
  }, [data, error, loading, onRefresh]);

  if (loading && !data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
        Loading branded scoreboard…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{
        padding: '14px 16px',
        background: '#fef2f2', border: '1px solid #fecaca',
        borderLeft: '4px solid #ef4444', borderRadius: 8,
        fontSize: 12, color: '#991b1b',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Scoreboard failed to load</div>
        <div>{error}</div>
      </div>
    );
  }
  if (!data) return null;

  if (!data.available) {
    return (
      <div style={{
        padding: '14px 16px',
        background: '#fffbeb', border: '1px solid #fde68a',
        borderLeft: '4px solid #f59e0b', borderRadius: 8,
        fontSize: 12, color: '#92400e',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Serper not configured</div>
        <div>{data.reason || 'SERPER_API_KEY missing on the deployed backend.'}</div>
      </div>
    );
  }

  const { terms = [], summary = { covered: 0, total: 0, missing: [] }, interlinking, ranAt } = data;

  return (
    <div>
      {/* Honest header — calibrates what the panel measures */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb',
        borderRadius: 12, padding: 14, marginBottom: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 2 }}>
              Branded Scoreboard
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
              Live Google top-10 organic results (US-geo), via Serper. Branded queries only.
              {ranAt && <> · Run {new Date(ranAt).toLocaleTimeString()}</>}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRefresh(true)}
            disabled={loading}
            style={{
              padding: '6px 12px',
              background: loading ? '#9ca3af' : '#3a6ad9',
              color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 12, fontWeight: 700,
              cursor: loading ? 'default' : 'pointer',
            }}>
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>

        {/* Coverage summary */}
        <div style={{
          background: '#f9fafb', border: '1px solid #f3f4f6',
          borderRadius: 8, padding: '10px 12px',
        }}>
          <div style={{ fontSize: 12, color: '#111827', marginBottom: 6 }}>
            <strong>{summary.covered}</strong> of <strong>{summary.total}</strong> owned properties appear in at least one branded SERP.
          </div>
          {summary.missing?.length > 0 && (
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              Never surfaced: {summary.missing.map((m) => (
                <span key={m.id} style={{
                  display: 'inline-block',
                  marginRight: 6, marginTop: 2,
                  padding: '2px 8px',
                  background: m.status === 'pending' ? '#fffbeb' : '#fef2f2',
                  color: m.status === 'pending' ? '#92400e' : '#991b1b',
                  border: `1px solid ${m.status === 'pending' ? '#fde68a' : '#fecaca'}`,
                  borderRadius: 999, fontSize: 11, fontWeight: 600,
                }}>
                  {m.name}{m.status === 'pending' ? ' (pending)' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Per-term rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {terms.map((t) => <ScoreboardTermRow key={t.term} term={t} />)}
      </div>

      {/* Interlinking honest-empty state */}
      {interlinking && !interlinking.available && (
        <div style={{
          marginTop: 18, padding: '12px 14px',
          background: '#f9fafb', border: '1px solid #e5e7eb',
          borderRadius: 8, fontSize: 11, color: '#6b7280', lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4 }}>Interlinking</div>
          {interlinking.note}
        </div>
      )}
    </div>
  );
}

function ScoreboardTermRow({ term }) {
  const [showOther, setShowOther] = useState(false);
  const cfg = term.note
    ? { tint: '#fffbeb', border: '#fde68a', accent: '#f59e0b' }
    : term.owned_count === 0
      ? { tint: '#fef2f2', border: '#fecaca', accent: '#ef4444' }
      : term.owned_count >= 5
        ? { tint: '#f0fdf4', border: '#86efac', accent: '#16a34a' }
        : { tint: '#fff', border: '#e5e7eb', accent: '#3a6ad9' };

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${cfg.border}`,
      borderLeft: `4px solid ${cfg.accent}`,
      borderRadius: 10, padding: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', fontFamily: 'monospace' }}>
            {term.term}
          </div>
          {term.note && (
            <div style={{ fontSize: 11, color: '#92400e', marginTop: 4 }}>{term.note}</div>
          )}
        </div>
        <div style={{
          fontSize: 11, fontWeight: 700, color: cfg.accent,
          background: cfg.tint, border: `1px solid ${cfg.border}`,
          borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap',
        }}>
          {term.owned_count} of {term.total || 10} owned
        </div>
      </div>

      {/* Owned slots */}
      {term.owned_results?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {term.owned_results.map((r) => {
            const posColor = r.position <= 3 ? '#16a34a' : '#3a6ad9';
            const posBg    = r.position <= 3 ? '#dcfce7' : '#eff6ff';
            return (
              <div key={`${r.property_id}:${r.position}`} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 10px', background: '#f9fafb',
                border: '1px solid #f3f4f6', borderRadius: 6,
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 800, color: posColor,
                  background: posBg, border: `1px solid ${posColor}30`,
                  borderRadius: 6, padding: '2px 8px', minWidth: 32, textAlign: 'center',
                }}>
                  #{r.position}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{r.property_name}</span>
                <a href={r.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 11, color: '#3a6ad9', fontFamily: 'monospace', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {r.url}
                </a>
              </div>
            );
          })}
        </div>
      )}

      {/* Other slots — collapsed by default */}
      {term.other_results?.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowOther((s) => !s)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 11, color: '#6b7280', fontWeight: 600,
              padding: 0, textAlign: 'left',
            }}>
            {showOther ? '▾' : '▸'} What else ranks ({term.other_results.length})
          </button>
          {showOther && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
              {term.other_results.map((r, i) => (
                <div key={`${r.position}:${i}`} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '5px 10px',
                  fontSize: 11, color: '#6b7280',
                }}>
                  <span style={{ fontFamily: 'monospace', color: '#9ca3af', minWidth: 28 }}>#{r.position}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title || r.link}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Interlinks panel ───────────────────────────────────────────────────
// Renders the owned → owned link matrix as a grid (rows = sources,
// columns = targets) + a per-source gap callout. Cell states:
//   linkExists=true  → green ✓ (auto-crawled or admin-declared)
//   linkExists=false → red ✗ (auto-crawl confirmed absent)
//   linkExists=null  → gray ? (no scan data yet — admin can manually
//                              toggle for non-crawlable sources)
// Non-crawlable sources (social platforms, Crunchbase, GMB) get a
// click-to-toggle checkbox on each cell since the crawl can't see their
// HTML. Crawlable sources (Homepage, App, Substack) get the auto-crawled
// state — clicking a cell on a crawlable source is a no-op (the state
// comes from the next /scan-interlinks run).
function InterlinksPanel({ data, loading, error, scanning, onLoad, onScan, onManualToggle }) {
  // Lazy-load on first tab visit.
  React.useEffect(() => {
    if (!data && !error && !loading) onLoad();
  }, [data, error, loading, onLoad]);

  if (loading && !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading interlinks…</div>;
  }
  if (error) {
    return (
      <div style={{
        padding: '14px 16px',
        background: '#fef2f2', border: '1px solid #fecaca',
        borderLeft: '4px solid #ef4444', borderRadius: 8,
        fontSize: 12, color: '#991b1b',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Interlinks failed to load</div>
        <div>{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { properties = [], matrix = [], gaps = [] } = data;

  // Quick lookup by (from, to) for the grid
  const cellByPair = new Map();
  for (const m of matrix) cellByPair.set(`${m.fromId}:${m.toId}`, m);

  // Active "high-priority" gaps: source = Homepage AND target is a live
  // external property with no link yet. Those are the homepage
  // authority-passing fixes the spec called out.
  const homepage = properties.find((p) => p.name === 'Homepage');
  const homepageGaps = homepage
    ? (gaps.find((g) => g.sourceId === homepage.id)?.missing || []).filter((m) => m.toStatus === 'live')
    : [];

  return (
    <div>
      {/* Header + Scan button + honest framing */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb',
        borderRadius: 12, padding: 14, marginBottom: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 2 }}>
              Owned Property Interlinks
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
              Auto-crawled links are verified from page HTML on each scan. Manual rows are self-declared because the platform's HTML doesn't reveal them (LinkedIn / Facebook / Instagram / X / Crunchbase / GMB).
            </div>
          </div>
          <button
            type="button"
            onClick={onScan}
            disabled={scanning}
            style={{
              padding: '6px 12px',
              background: scanning ? '#9ca3af' : '#3a6ad9',
              color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 12, fontWeight: 700,
              cursor: scanning ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}>
            {scanning ? 'Scanning…' : '🔍 Run Scan'}
          </button>
        </div>

        {/* Homepage gaps callout — the actionable surface */}
        {homepageGaps.length > 0 && (
          <div style={{
            marginTop: 10, padding: '10px 12px',
            background: '#fffbeb', border: '1px solid #fde68a',
            borderLeft: '4px solid #f59e0b', borderRadius: 7,
            fontSize: 12, color: '#92400e',
          }}>
            <strong>Homepage authority gaps:</strong> {' '}
            <span>doesn't link to {homepageGaps.map((g) => g.toName).join(', ')}. Adding these links from godoorly.com is the cheapest authority-passing fix for the properties that aren't ranking.</span>
          </div>
        )}
      </div>

      {/* Matrix grid. Columns: properties (targets). Rows: properties
          (sources). Cell color/symbol per linkExists state. */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb',
        borderRadius: 12, padding: 12, marginBottom: 14, overflowX: 'auto',
      }}>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, fontWeight: 600 }}>
          ROWS = FROM (source) · COLUMNS = TO (target). Click cells on non-crawlable rows to toggle manual state.
        </div>
        <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 10px', textAlign: 'left', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10, borderBottom: '1px solid #e5e7eb' }}>From / To</th>
              {properties.map((p) => (
                <th key={p.id} style={{ padding: '6px 8px', textAlign: 'center', color: '#374151', fontWeight: 700, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                  {p.name}
                  {p.status === 'pending' && <span style={{ display: 'block', fontSize: 9, color: '#f59e0b', fontWeight: 600 }}>pending</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {properties.map((src) => (
              <tr key={src.id}>
                <td style={{ padding: '6px 10px', fontWeight: 700, color: '#111827', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                  {src.name}
                  <div style={{ fontSize: 9, color: src.crawlable ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
                    {src.crawlable ? 'auto-crawled' : 'manual'}
                  </div>
                </td>
                {properties.map((tgt) => {
                  if (src.id === tgt.id) {
                    return (
                      <td key={tgt.id} style={{ padding: '6px 8px', textAlign: 'center', color: '#d1d5db', borderBottom: '1px solid #f3f4f6' }}>—</td>
                    );
                  }
                  const cell = cellByPair.get(`${src.id}:${tgt.id}`);
                  const state = cell?.linkExists; // true | false | null
                  const isManual = !src.crawlable;
                  let bg, fg, sym;
                  if (state === true)       { bg = '#dcfce7'; fg = '#166534'; sym = '✓'; }
                  else if (state === false) { bg = '#fee2e2'; fg = '#991b1b'; sym = '✗'; }
                  else                      { bg = '#f9fafb'; fg = '#9ca3af'; sym = '?'; }
                  return (
                    <td key={tgt.id} style={{
                      padding: '4px 6px', textAlign: 'center',
                      borderBottom: '1px solid #f3f4f6',
                    }}>
                      <button
                        type="button"
                        disabled={!isManual}
                        onClick={() => {
                          if (!isManual) return;
                          onManualToggle(src.id, tgt.id, state !== true);
                        }}
                        title={isManual
                          ? (state === true ? 'Click to mark absent' : 'Click to mark present')
                          : `auto-crawled · ${cell?.lastChecked ? new Date(cell.lastChecked).toLocaleString() : 'no scan yet'}`}
                        style={{
                          minWidth: 32, padding: '4px 8px',
                          background: bg, color: fg,
                          border: '1px solid ' + (state === true ? '#86efac' : state === false ? '#fecaca' : '#e5e7eb'),
                          borderRadius: 4, fontSize: 12, fontWeight: 700,
                          cursor: isManual ? 'pointer' : 'default',
                        }}>
                        {sym}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-source gaps list — text version of the same data, easier
          to read on narrow screens than the matrix. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {gaps.map((g) => {
          const noGaps = g.missing.length === 0;
          return (
            <div key={g.sourceId} style={{
              background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: 8, padding: '10px 12px',
              fontSize: 12,
            }}>
              <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                {g.sourceName}
                <span style={{ fontSize: 10, color: g.sourceCrawlable ? '#16a34a' : '#9ca3af', marginLeft: 8, fontWeight: 600 }}>
                  {g.sourceCrawlable ? 'auto-crawled' : 'manual'}
                </span>
              </div>
              {noGaps ? (
                <div style={{ fontSize: 11, color: '#16a34a' }}>Links to every other owned property. ✓</div>
              ) : (
                <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
                  Does not link to: {g.missing.map((m) => (
                    <span key={m.toId} style={{
                      display: 'inline-block',
                      marginRight: 6, marginTop: 2,
                      padding: '2px 8px',
                      background: m.state === 'confirmed_absent' ? '#fef2f2' : '#f9fafb',
                      color: m.state === 'confirmed_absent' ? '#991b1b' : '#6b7280',
                      border: `1px solid ${m.state === 'confirmed_absent' ? '#fecaca' : '#e5e7eb'}`,
                      borderRadius: 999, fontSize: 11, fontWeight: 600,
                    }}>
                      {m.toName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Technical Audit panel ──────────────────────────────────────────────
// Per-page scorecards combining PSI Lighthouse (performance, SEO,
// accessibility, best-practices — mobile + desktop) with seoAudit's
// indexability verdict. Color thresholds match Lighthouse:
//   green ≥90, amber 50-89, red <50.  Null score = audit didn't run for
// that category (rate-limited keyless PSI, network failure, etc.) —
// rendered as a neutral 'unavailable' chip, NOT a 0. The distinction is
// the whole point: an honest 'we couldn't measure' vs. a fake failure.
function TechnicalAuditPanel({ data, loading, error, running, onLoad, onRun }) {
  // Lazy-load on first tab visit.
  useEffect(() => {
    if (!data && !error && !loading) onLoad();
  }, [data, error, loading, onLoad]);

  const scorecards = data?.scorecards || [];
  const hasAny = scorecards.some((s) => !!s.result);
  const lastRanAt = scorecards
    .map((s) => s.result?.ran_at || s.ran_at)
    .filter(Boolean)
    .sort()
    .reverse()[0] || null;

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 14, gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#111827', marginBottom: 4 }}>
            Technical Audit — Core Marketing Pages
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 720, lineHeight: 1.55 }}>
            Google Lighthouse via the PageSpeed Insights API (mobile + desktop) combined
            with on-page indexability checks (status / noindex / canonical / sitemap / robots).
            Many performance issues on Webflow sites are platform-level — focus on image
            optimization, unused JS/CSS, and asset hygiene.
            {lastRanAt && (
              <span style={{ display: 'block', marginTop: 4, color: '#9ca3af' }}>
                Last run: {new Date(lastRanAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <button
          disabled={running || loading}
          onClick={onRun}
          style={{
            background: running ? '#9ca3af' : '#3a6ad9',
            color: '#fff', fontWeight: 700, fontSize: 13,
            border: 'none', borderRadius: 8, padding: '10px 18px',
            cursor: running ? 'wait' : 'pointer',
            whiteSpace: 'nowrap',
          }}>
          {running ? 'Running… (~30s per page)' : (hasAny ? 'Re-run Technical Audit' : 'Run Technical Audit')}
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fff0f3', border: '1px solid #fecdd3', borderLeft: '4px solid #ff082f',
          borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 12, color: '#9f1239',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Technical Audit failed</div>
          <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{String(error)}</div>
        </div>
      )}

      {loading && !error && (
        <div style={{ color: '#6b7280', fontSize: 13 }}>Loading scorecards…</div>
      )}

      {!loading && !error && !scorecards.length && (
        <div style={{
          background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8,
          padding: 24, fontSize: 13, color: '#6b7280', textAlign: 'center',
        }}>
          No core pages configured. Seed runs at boot — check the server logs for the
          <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 4, margin: '0 4px' }}>seo_core_pages</code>
          table.
        </div>
      )}

      {!loading && !error && scorecards.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {scorecards.map((sc) => <TechnicalAuditScorecard key={sc.page_id} scorecard={sc} />)}
        </div>
      )}

      {/* Honest framing — visible at the bottom so the strategist
          doesn't take Lighthouse scores as gospel. */}
      {!loading && !error && hasAny && (
        <div style={{
          marginTop: 16, padding: '12px 14px', fontSize: 11, color: '#6b7280',
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
          lineHeight: 1.55,
        }}>
          <strong style={{ color: '#374151' }}>How to read these scores:</strong> Lighthouse
          measures one synthetic load from Google's data center, not real-user metrics.
          Score thresholds: green ≥90, amber 50–89, red &lt; 50. A null score (gray)
          means PSI couldn't return that category — usually rate-limit (set <code>GOOGLE_PAGESPEED_API_KEY</code>
          in Render env to fix) or a network blip. Indexability checks (verdict column)
          come from our own crawler, not Google.
        </div>
      )}
    </div>
  );
}

function TechnicalAuditScorecard({ scorecard }) {
  const { name, url, is_priority, result } = scorecard;
  const mobile  = result?.mobile  || null;
  const desktop = result?.desktop || null;
  const idx     = result?.indexability || null;
  const ranAt   = result?.ran_at || scorecard.ran_at;

  return (
    <div style={{
      background: '#fff',
      border: is_priority ? '2px solid #3a6ad9' : '1px solid #e5e7eb',
      borderRadius: 10, padding: '16px 18px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{name}</span>
            {is_priority && (
              <span style={{
                background: '#ffdd86', color: '#111827',
                fontSize: 10, fontWeight: 700, padding: '2px 8px',
                borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>Priority</span>
            )}
          </div>
          <a href={url} target="_blank" rel="noreferrer"
             style={{ fontSize: 11, color: '#3a6ad9', textDecoration: 'none', fontFamily: 'monospace' }}>
            {url} ↗
          </a>
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af', textAlign: 'right' }}>
          {ranAt ? `Audited ${new Date(ranAt).toLocaleString()}` : 'Not yet audited'}
        </div>
      </div>

      {/* Indexability verdict row */}
      <div style={{ marginBottom: 12 }}>
        <IndexabilityChip verdict={idx} />
      </div>

      {/* Lighthouse score grids — mobile + desktop side-by-side */}
      {!result && (
        <div style={{
          padding: 16, fontSize: 12, color: '#9ca3af',
          background: '#f9fafb', border: '1px dashed #e5e7eb',
          borderRadius: 8, textAlign: 'center',
        }}>
          Click "Run Technical Audit" above to fetch Lighthouse scores for this page.
        </div>
      )}
      {result && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <LighthouseColumn label="Mobile"  scores={mobile} />
          <LighthouseColumn label="Desktop" scores={desktop} />
        </div>
      )}

      {/* Top issues — merged mobile+desktop, deduped by audit id */}
      {result && (mobile?.top_issues?.length || desktop?.top_issues?.length) > 0 && (
        <TopIssuesAccordion mobile={mobile} desktop={desktop} />
      )}
    </div>
  );
}

function IndexabilityChip({ verdict }) {
  if (!verdict) {
    return (
      <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 4 }}>
        Indexability: not yet checked
      </span>
    );
  }
  // Verdict space mirrors seoAudit.js
  const map = {
    blocked:           { bg: '#fee2e2', color: '#991b1b', label: 'Blocked from indexing' },
    canonical_issue:   { bg: '#ffdd86', color: '#7a5200', label: 'Canonical mismatch' },
    not_in_sitemap:    { bg: '#ffdd86', color: '#7a5200', label: 'Not in sitemap' },
    unreachable:       { bg: '#fee2e2', color: '#991b1b', label: 'Unreachable' },
    healthy_pending:   { bg: '#dcfce7', color: '#166534', label: 'Indexability clean' },
  };
  const v = verdict.verdict || verdict;
  const cfg = map[v] || { bg: '#f3f4f6', color: '#374151', label: `Verdict: ${v || 'unknown'}` };
  return (
    <div>
      <span style={{
        background: cfg.bg, color: cfg.color,
        fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 4,
      }}>
        {cfg.label}
      </span>
      {verdict.reason && (
        <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280' }}>· {verdict.reason}</span>
      )}
    </div>
  );
}

function LighthouseColumn({ label, scores }) {
  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        {label}
      </div>
      {!scores && (
        <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
          PSI unavailable (likely rate-limit — see footer note)
        </div>
      )}
      {scores && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <ScoreCell label="Performance"   score={scores.performance} />
          <ScoreCell label="SEO"           score={scores.seo} />
          <ScoreCell label="Accessibility" score={scores.accessibility} />
          <ScoreCell label="Best Pract."   score={scores.best_practices} />
        </div>
      )}
    </div>
  );
}

function ScoreCell({ label, score }) {
  // Lighthouse color thresholds: green ≥90, amber 50-89, red <50, gray null.
  const cfg = (() => {
    if (score === null || score === undefined) return { bg: '#f3f4f6', color: '#6b7280', label: '—' };
    if (score >= 90) return { bg: '#dcfce7', color: '#166534', label: String(score) };
    if (score >= 50) return { bg: '#ffdd86', color: '#7a5200', label: String(score) };
    return { bg: '#fee2e2', color: '#991b1b', label: String(score) };
  })();
  return (
    <div style={{
      background: cfg.bg, borderRadius: 6, padding: '6px 8px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: cfg.color, fontFamily: 'monospace' }}>
        {cfg.label}
      </div>
      <div style={{ fontSize: 9, color: cfg.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
    </div>
  );
}

function TopIssuesAccordion({ mobile, desktop }) {
  const [open, setOpen] = useState(false);
  // Merge + dedupe by audit id; mobile wins on tie (mobile scores
  // matter more for Google's mobile-first index).
  const byId = new Map();
  for (const i of (mobile?.top_issues  || [])) byId.set(i.id, { ...i, sources: ['mobile'] });
  for (const i of (desktop?.top_issues || [])) {
    if (byId.has(i.id)) byId.get(i.id).sources.push('desktop');
    else byId.set(i.id, { ...i, sources: ['desktop'] });
  }
  const merged = [...byId.values()].sort((a, b) => a.score - b.score);
  if (!merged.length) return null;
  return (
    <div style={{ marginTop: 12, borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 12, fontWeight: 700, color: '#3a6ad9', padding: 0,
      }}>
        {open ? '▼' : '▶'} Top issues ({merged.length})
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {merged.map(issue => (
            <div key={issue.id} style={{
              background: '#f9fafb', border: '1px solid #e5e7eb',
              borderRadius: 6, padding: '8px 10px',
              fontSize: 11, lineHeight: 1.5,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                <span style={{ fontWeight: 700, color: '#111827' }}>{issue.title}</span>
                <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 10, whiteSpace: 'nowrap' }}>
                  {issue.sources.join('+')} · score {issue.score.toFixed(2)}
                </span>
              </div>
              {issue.displayValue && (
                <div style={{ color: '#7a5200', fontFamily: 'monospace', fontSize: 10, marginTop: 2 }}>
                  {issue.displayValue}
                </div>
              )}
              {issue.description && (
                <div style={{ color: '#6b7280', marginTop: 4 }}>{issue.description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Keyword Discovery panel ────────────────────────────────────────────
// Seed → Serper /search peopleAlsoAsk + relatedSearches + /autocomplete
// → deduped candidates with already-tracked flags → inline +Add that
// reuses the existing POST /keywords endpoint with a target_url picked
// from the Webflow page + blog dropdown (not free-text — that's what
// produced aspirational targets that the classifier later flagged as
// broken).
//
// CRITICAL HONESTY NOTE
// Serper doesn't return search volume on these surfaces. We deliberately
// do NOT fabricate or estimate a volume column. Volume signal lives in
// GSC + Google Keyword Planner; this panel is for DISCOVERY, not demand
// quantification.
function DiscoveryPanel({
  seeds, setSeeds,
  result, error, running,
  onDiscover,
  targetOptions, onLoadTargetOptions,
  addingCandidate, setAddingCandidate,
  addForm, setAddForm,
  onAddKeyword,
}) {
  const [newSeed, setNewSeed] = useState('');
  const addSeed = () => {
    const t = newSeed.trim();
    if (!t) return;
    if (seeds.includes(t)) { setNewSeed(''); return; }
    setSeeds([...seeds, t]);
    setNewSeed('');
  };
  const removeSeed = (s) => setSeeds(seeds.filter(x => x !== s));

  // Group candidates by source for rendering. The backend already sorts
  // untracked-first within source, so we preserve order inside each
  // bucket and just present them under section headers.
  const candidates = result?.candidates || [];
  const byGroup = {
    paa:          candidates.filter(c => c.source === 'paa'),
    related:      candidates.filter(c => c.source === 'related'),
    autocomplete: candidates.filter(c => c.source === 'autocomplete'),
  };
  const SOURCE_LABEL = {
    paa:          'People Also Ask',
    related:      'Related Searches',
    autocomplete: 'Autocomplete',
  };

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#111827', marginBottom: 4 }}>
          Keyword Discovery
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 720, lineHeight: 1.55 }}>
          Enter seed terms. We pull Google's <strong>People Also Ask</strong>,
          <strong> Related Searches</strong>, and <strong>autocomplete</strong> suggestions
          via Serper — the queries Google itself surfaces alongside each seed. These are
          real searches by real users. Click <strong>+ Add</strong> on any candidate to
          start tracking it. Volume is intentionally omitted (Serper doesn't return it
          and we won't fake it — check GSC for measured demand on tracked keywords).
        </div>
      </div>

      {/* Seed chips + input */}
      <div style={{
        background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: '14px 16px', marginBottom: 16,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Seed terms ({seeds.length})
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {seeds.map(s => (
            <span key={s} style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999,
              padding: '4px 4px 4px 12px', fontSize: 12,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {s}
              <button onClick={() => removeSeed(s)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9ca3af', fontSize: 14, padding: '0 6px',
                }}
                title="Remove seed">×</button>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={newSeed}
            onChange={e => setNewSeed(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSeed(); } }}
            placeholder="Add a seed term and press Enter"
            style={{
              flex: 1, padding: '8px 12px', fontSize: 13,
              border: '1px solid #d1d5db', borderRadius: 6,
            }}
          />
          <button onClick={addSeed} style={{
            background: '#111827', color: '#fff', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Add seed</button>
          <button
            disabled={running || !seeds.length}
            onClick={onDiscover}
            style={{
              background: running ? '#9ca3af' : '#3a6ad9',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 20px', fontSize: 13, fontWeight: 700,
              cursor: running ? 'wait' : 'pointer',
            }}>
            {running ? 'Discovering…' : 'Discover Candidates'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fff0f3', border: '1px solid #fecdd3', borderLeft: '4px solid #ff082f',
          borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 12, color: '#9f1239',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Discovery failed</div>
          <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{String(error)}</div>
        </div>
      )}

      {/* Per-seed errors (Serper degraded for SOME seeds but not all) */}
      {result?.per_seed && result.per_seed.some(p => p.error) && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '4px solid #d97706',
          borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 11, color: '#92400e',
        }}>
          <strong>Partial result:</strong>{' '}
          {result.per_seed.filter(p => p.error).map(p => p.seed).join(', ')}
          {' '}returned no candidates (Serper error or rate-limit).
        </div>
      )}

      {result && !error && (
        <div style={{ marginBottom: 14, fontSize: 12, color: '#6b7280' }}>
          <strong style={{ color: '#111827' }}>{result.total}</strong> candidate{result.total === 1 ? '' : 's'} ·{' '}
          <strong style={{ color: '#3a6ad9' }}>{result.new_count}</strong> new ·{' '}
          {result.total - result.new_count} already tracked ·{' '}
          ran {new Date(result.ran_at).toLocaleString()}
        </div>
      )}

      {result && Object.entries(byGroup).map(([source, items]) => {
        if (!items.length) return null;
        return (
          <div key={source} style={{ marginBottom: 18 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#374151',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{
                background: source === 'paa' ? '#3a6ad9' : source === 'related' ? '#7c3aed' : '#0891b2',
                color: '#fff', padding: '2px 10px', borderRadius: 4, fontSize: 10,
              }}>
                {SOURCE_LABEL[source]}
              </span>
              <span style={{ color: '#6b7280', fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>
                {items.length} candidate{items.length === 1 ? '' : 's'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {items.map(c => (
                <CandidateRow
                  key={`${source}:${c.query}`}
                  candidate={c}
                  isAdding={addingCandidate?.query === c.query}
                  onStartAdd={() => {
                    setAddForm({ target_url: '', intent: 'informational', market: 'both', priority: 'medium', notes: '' });
                    setAddingCandidate(c);
                    if (targetOptions === null) onLoadTargetOptions();
                  }}
                  onCancelAdd={() => setAddingCandidate(null)}
                  addForm={addForm}
                  setAddForm={setAddForm}
                  targetOptions={targetOptions}
                  onSubmitAdd={() => onAddKeyword(c)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {result && result.total === 0 && (
        <div style={{
          background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8,
          padding: 24, fontSize: 13, color: '#6b7280', textAlign: 'center',
        }}>
          No candidates returned. Try broader seeds (e.g. 'home loan' instead of 'home loan after bankruptcy specific Texas') —
          Google surfaces PAA/Related only on queries with sufficient SERP intent.
        </div>
      )}
    </div>
  );
}

function CandidateRow({ candidate, isAdding, onStartAdd, onCancelAdd, addForm, setAddForm, targetOptions, onSubmitAdd }) {
  return (
    <div style={{
      background: candidate.already_tracked ? '#f3f4f6' : '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '10px 12px',
      opacity: candidate.already_tracked ? 0.65 : 1,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: '#111827', wordBreak: 'break-word' }}>
            {candidate.query}
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
            from seed: <span style={{ fontFamily: 'monospace' }}>{candidate.found_via_seed}</span>
          </div>
        </div>
        {candidate.already_tracked ? (
          <span style={{
            background: '#dcfce7', color: '#166534', fontSize: 10, fontWeight: 700,
            padding: '3px 10px', borderRadius: 4,
          }}>
            ALREADY TRACKED
          </span>
        ) : isAdding ? (
          <button onClick={onCancelAdd} style={{
            background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
            padding: '6px 14px', fontSize: 11, color: '#6b7280', cursor: 'pointer',
          }}>Cancel</button>
        ) : (
          <button onClick={onStartAdd} style={{
            background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6,
            padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>+ Add</button>
        )}
      </div>

      {/* Inline add form — same fields as the Keywords tab add form. */}
      {isAdding && (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
        }}>
          <label style={{ gridColumn: '1 / -1', fontSize: 11, color: '#374151' }}>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>Target URL</div>
            {targetOptions === null ? (
              <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', padding: '6px 0' }}>
                Loading real Webflow pages + blog posts…
              </div>
            ) : targetOptions.length === 0 ? (
              <input type="text" value={addForm.target_url}
                onChange={e => setAddForm({ ...addForm, target_url: e.target.value })}
                placeholder="/blog-posts/... (Webflow list empty — type a path)"
                style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }} />
            ) : (
              <select value={addForm.target_url}
                onChange={e => setAddForm({ ...addForm, target_url: e.target.value })}
                style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}>
                <option value="">— pick a real page or blog post —</option>
                {/* Group via optgroup so the user sees pages vs blog. */}
                <optgroup label="Pages">
                  {targetOptions.filter(o => o.kind === 'page').map(o => (
                    <option key={o.url} value={o.url}>{o.label} · {o.url}</option>
                  ))}
                </optgroup>
                <optgroup label="Blog Posts">
                  {targetOptions.filter(o => o.kind === 'blog').map(o => (
                    <option key={o.url} value={o.url}>{o.label}</option>
                  ))}
                </optgroup>
              </select>
            )}
          </label>
          <label style={{ fontSize: 11, color: '#374151' }}>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>Intent</div>
            <select value={addForm.intent} onChange={e => setAddForm({ ...addForm, intent: e.target.value })}
              style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}>
              <option value="informational">Informational</option>
              <option value="commercial">Commercial</option>
              <option value="transactional">Transactional</option>
              <option value="navigational">Navigational</option>
            </select>
          </label>
          <label style={{ fontSize: 11, color: '#374151' }}>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>Priority</div>
            <select value={addForm.priority} onChange={e => setAddForm({ ...addForm, priority: e.target.value })}
              style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label style={{ gridColumn: '1 / -1', fontSize: 11, color: '#374151' }}>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>Notes (optional)</div>
            <input type="text" value={addForm.notes}
              onChange={e => setAddForm({ ...addForm, notes: e.target.value })}
              placeholder={`Discovered via Serper ${candidate.source.toUpperCase()} — seed: "${candidate.found_via_seed}"`}
              style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }} />
          </label>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={onSubmitAdd}
              disabled={!addForm.target_url}
              style={{
                background: addForm.target_url ? '#acf44e' : '#e5e7eb',
                color: addForm.target_url ? '#111827' : '#9ca3af',
                border: 'none', borderRadius: 6, padding: '8px 16px',
                fontSize: 12, fontWeight: 700,
                cursor: addForm.target_url ? 'pointer' : 'not-allowed',
              }}>
              Save tracked keyword
            </button>
            <button onClick={onCancelAdd}
              style={{
                background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                padding: '8px 16px', fontSize: 12, color: '#6b7280', cursor: 'pointer',
              }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminSEO() {
  const [activeTab, setActiveTab]     = useState('overview');
  const [status, setStatus]           = useState(null);
  const [overview, setOverview]       = useState(null);
  const [keywords, setKeywords]       = useState([]);
  const [backlinks, setBacklinks]     = useState(null);
  // Webflow blog posts surface — { posts: [...], available: bool, reason? }
  // Rendered at the bottom of the Keywords tab so the strategist can see
  // every published blog URL alongside the keyword target list. Wiring
  // a blog URL into seo_keywords for GSC tracking is still manual via
  // POST /seo/keywords.
  const [blogPosts, setBlogPosts]     = useState({ posts: [], available: true });
  // Crawl audit state — POST /run-crawl-audit returns
  //   { audited, summary: { blocked, canonical_issue, not_in_sitemap,
  //     unreachable, healthy_pending }, results: [...], ranAt }
  // Cleared between runs so a previous failure doesn't ghost.
  const [crawlAuditing,   setCrawlAuditing]   = useState(false);
  const [crawlAuditResult, setCrawlAuditResult] = useState(null);
  const [crawlAuditError, setCrawlAuditError] = useState(null);
  // Brand scoreboard — GET /brand-scoreboard returns
  //   { terms: [{ term, owned_results, other_results, ... }],
  //     summary: { covered, total, missing }, owned_properties, ... }
  // Loaded on Scoreboard tab activation; Refresh button re-pulls with
  // ?refresh=1 to bust the 24h serper cache.
  const [scoreboard, setScoreboard]           = useState(null);
  const [scoreboardLoading, setScoreboardLoading] = useState(false);
  const [scoreboardError, setScoreboardError] = useState(null);
  // Interlinks tab — GET /interlinks returns { properties, matrix, gaps }.
  // scanning flag wraps the POST /scan-interlinks call.
  const [interlinks, setInterlinks]           = useState(null);
  const [interlinksLoading, setInterlinksLoading] = useState(false);
  const [interlinksError, setInterlinksError] = useState(null);
  const [interlinksScanning, setInterlinksScanning] = useState(false);
  // Technical Audit tab — GET /technical-audit returns
  //   { scorecards: [{ page_id, name, url, is_priority, ran_at, result }] }
  // where result is null for pages that haven't been audited yet, or
  //   { mobile, desktop, indexability, ran_at, psi_keyed, ... } once run.
  // running flag wraps POST /run-technical-audit (slow — 30s per page).
  const [technicalAudit, setTechnicalAudit]               = useState(null);
  const [technicalAuditLoading, setTechnicalAuditLoading] = useState(false);
  const [technicalAuditError, setTechnicalAuditError]     = useState(null);
  const [technicalAuditRunning, setTechnicalAuditRunning] = useState(false);
  // Keyword Discovery tab — seeds are user-editable, prefilled with the
  // niche set the AFM™ audience actually searches around (denial,
  // self-employed, non-QM, alt-credit). discoverResult holds the
  // /discover-keywords response. targetOptions is lazy-loaded from
  // /discover-target-options on first +Add click so the dropdown
  // populates with real Webflow page + blog URLs (NOT free-text guesses
  // — that's what got us aspirational targets in the first place).
  const [discoverySeeds, setDiscoverySeeds] = useState([
    'mortgage denied',
    'denied home loan',
    'self employed mortgage',
    '1099 mortgage',
    'bad credit home loan',
    'no minimum FICO mortgage',
    'non qm loan',
    'owner finance home',
    'buy house bad credit',
    'gig worker mortgage',
  ]);
  const [discoverResult, setDiscoverResult]   = useState(null);
  const [discoverRunning, setDiscoverRunning] = useState(false);
  const [discoverError, setDiscoverError]     = useState(null);
  const [targetOptions, setTargetOptions]     = useState(null);   // null = not yet loaded, [] = loaded empty
  const [addingCandidate, setAddingCandidate] = useState(null);   // the candidate currently being added (inline form)
  const [addForm, setAddForm] = useState({
    target_url: '', intent: 'informational',
    market: 'both', priority: 'medium', notes: '',
  });
  // Inline keyword edit. editingKwId = null when no row is open;
  // editKwForm mirrors the add-form shape so the same input controls
  // can render in either mode.
  const [editingKwId, setEditingKwId] = useState(null);
  const [editKwForm,  setEditKwForm]  = useState({
    keyword: '', intent: 'transactional', market: 'both',
    priority: 'medium', target_url: '', notes: '',
  });
  // audits state removed — the Audits tab (single-row aggregate
  // GSC metrics duplicated by Overview) was retired. The GET
  // /api/admin/seo/audits endpoint stays callable on the backend
  // (the seo_audits table is also read by /generate-recommendations
  // when Claude analyzes ranking trends), so nothing else breaks.
  const [recs, setRecs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [syncing, setSyncing]         = useState(false);
  const [auditing, setAuditing]       = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [overviewDays, setOverviewDays] = useState('28');
  const [showAddKeyword, setShowAddKeyword] = useState(false);
  const [showAddBacklink, setShowAddBacklink] = useState(false);
  const [kwForm, setKwForm] = useState({
    keyword: '', intent: 'transactional',
    market: 'both', priority: 'medium',
    target_url: '', notes: '',
  });
  const [blForm, setBlForm] = useState({
    domain: '', page_url: '', relevance: 'medium',
    domain_authority: '', notes: '',
  });

  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      // /audits removed from the boot fetch — the Audits tab that
      // consumed it is gone. Endpoint stays callable for
      // /generate-recommendations server-side consumption; not
      // needed on tab activation here anymore.
      const [statusRes, kwRes, blRes, recRes, blogRes] =
        await Promise.allSettled([
          axios.get(`${API}/api/admin/seo/status`, { headers }),
          axios.get(`${API}/api/admin/seo/keywords`, { headers }),
          axios.get(`${API}/api/admin/seo/backlinks`, { headers }),
          axios.get(`${API}/api/admin/seo/recommendations`, { headers }),
          axios.get(`${API}/api/admin/seo/blog-posts`, { headers }),
        ]);

      if (statusRes.status === 'fulfilled') setStatus(statusRes.value.data);
      if (kwRes.status === 'fulfilled') setKeywords(kwRes.value.data || []);
      if (blRes.status === 'fulfilled') setBacklinks(blRes.value.data);
      if (recRes.status === 'fulfilled') setRecs(recRes.value.data || []);
      // Blog posts — degrade-soft on rejection or available=false.
      if (blogRes.status === 'fulfilled') {
        setBlogPosts(blogRes.value.data || { posts: [], available: true });
      } else {
        setBlogPosts({ posts: [], available: false, reason: 'fetch failed' });
      }
    } catch (err) {
      console.error('SEO fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async (days) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/seo/overview?days=${days}`,
        { headers }
      );
      setOverview(res.data);
    } catch (err) {
      console.error('Overview fetch failed:', err.message);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchOverview('28');
  }, []); // eslint-disable-line

  const tabStyle = (tab) => ({
    padding: '8px 18px', border: 'none', background: 'none',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    color: activeTab === tab ? '#111827' : '#9ca3af',
    borderBottom: activeTab === tab
      ? '2px solid #111827' : '2px solid transparent',
    marginBottom: -2,
  });

  const Pill = ({ text, config }) => (
    <span style={{
      fontSize: 10, fontWeight: 600,
      background: config?.bg || '#f3f4f6',
      color: config?.color || '#374151',
      borderRadius: 999, padding: '2px 8px',
      whiteSpace: 'nowrap',
    }}>
      {config?.label || text}
    </span>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 20,
        }}>
          <div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>
              Marketing
            </div>
            <h1 style={{
              fontSize: 22, fontWeight: 700,
              color: '#111827', margin: 0,
            }}>
              SEO Agent
            </h1>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={syncing}
              onClick={async () => {
                setSyncing(true);
                try {
                  await axios.post(
                    `${API}/api/admin/seo/sync`, {}, { headers }
                  );
                  await fetchAll();
                  await fetchOverview(overviewDays);
                } catch (err) {
                  alert(err.response?.data?.error || err.message);
                } finally { setSyncing(false); }
              }}
              style={{
                background: syncing ? '#9ca3af' : '#fff',
                color: '#374151', border: '1px solid #e5e7eb',
                borderRadius: 8, padding: '7px 14px',
                fontSize: 12, fontWeight: 600,
                cursor: syncing ? 'default' : 'pointer',
              }}>
              {syncing ? 'Syncing...' : 'Sync Rankings'}
            </button>
            <button
              disabled={auditing}
              onClick={async () => {
                setAuditing(true);
                try {
                  await axios.post(
                    `${API}/api/admin/seo/run-audit`, {}, { headers }
                  );
                  await fetchAll();
                } catch (err) {
                  alert(err.response?.data?.error || err.message);
                } finally { setAuditing(false); }
              }}
              style={{
                background: auditing ? '#9ca3af' : '#fff',
                color: '#374151', border: '1px solid #e5e7eb',
                borderRadius: 8, padding: '7px 14px',
                fontSize: 12, fontWeight: 600,
                cursor: auditing ? 'default' : 'pointer',
              }}>
              {auditing ? 'Running...' : 'Run Audit'}
            </button>
            <button
              disabled={generating}
              onClick={async () => {
                setGenerating(true);
                try {
                  await axios.post(
                    `${API}/api/admin/seo/generate-recommendations`,
                    {}, { headers }
                  );
                  await fetchAll();
                  setActiveTab('recommendations');
                } catch (err) {
                  alert(err.response?.data?.error || err.message);
                } finally { setGenerating(false); }
              }}
              style={{
                background: generating ? '#9ca3af' : '#3a6ad9',
                color: '#fff', border: 'none',
                borderRadius: 8, padding: '7px 14px',
                fontSize: 12, fontWeight: 600,
                cursor: generating ? 'default' : 'pointer',
              }}>
              {generating ? 'Analyzing...' : 'Generate Recommendations'}
            </button>
          </div>
        </div>

        {/* Connection status — pulls the missing-env list directly off
            the /status response so the message reflects whichever env
            vars Render is actually missing (used to be hardcoded to
            GOOGLE_SEARCH_CONSOLE_REFRESH_TOKEN even when CLIENT_ID was
            the real problem). */}
        {status && (
          <div style={{
            background: status.connected ? '#f0fdf4' : '#fef9c3',
            border: `1px solid ${status.connected ? '#86efac' : '#fde68a'}`,
            borderRadius: 10, padding: '10px 16px',
            marginBottom: 16, fontSize: 12,
            color: status.connected ? '#166534' : '#854d0e',
            fontWeight: 600,
          }}>
            {status.connected ? (
              <>Google Search Console Connected — {status.site_url}
                {status.permission_level && (
                  <span style={{ marginLeft: 6, opacity: 0.7 }}>
                    ({status.permission_level})
                  </span>
                )}
              </>
            ) : (
              <>
                <div>Google Search Console not connected.</div>
                {Array.isArray(status.missing) && status.missing.length > 0 && (
                  <div style={{ marginTop: 4, fontWeight: 400 }}>
                    Missing on Render:{' '}
                    {status.missing.map((m, i) => (
                      <span key={i}>
                        {i > 0 && ', '}
                        <code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 4px', borderRadius: 3 }}>{m}</code>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 0,
          borderBottom: '2px solid #e5e7eb',
          marginBottom: 20,
        }}>
          {[
            { id: 'overview',         label: 'Overview' },
            { id: 'keywords',         label: `Keywords (${keywords.length})` },
            { id: 'discovery',        label: 'Discovery' },
            { id: 'scoreboard',       label: 'Scoreboard' },
            { id: 'interlinks',       label: 'Interlinks' },
            { id: 'technical',        label: 'Technical Audit' },
            { id: 'backlinks',        label: `Backlinks (${backlinks?.summary?.total || 0})` },
            // 'audits' tab removed — was a single-row aggregate GSC
            // metrics table duplicating what Overview already shows.
            // Crawl Audit lives on Keywords; Technical Audit is its
            // own tab; both remain.
            { id: 'recommendations',  label: `Recommendations (${recs.filter(r => r.status === 'pending_review').length})` },
          ].map(tab => (
            <button key={tab.id}
              style={tabStyle(tab.id)}
              onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            Loading...
          </div>
        )}

        {/* ── Overview Tab ─────────────────────────────────────────── */}
        {!loading && activeTab === 'overview' && (
          <div>
            {/* Period selector */}
            <div style={{
              display: 'flex', gap: 6, marginBottom: 16,
            }}>
              {[
                { value: '7',  label: 'Last 7 days' },
                { value: '28', label: 'Last 28 days' },
                { value: '90', label: 'Last 90 days' },
              ].map(d => (
                <button key={d.value}
                  onClick={() => {
                    setOverviewDays(d.value);
                    fetchOverview(d.value);
                  }}
                  style={{
                    padding: '5px 12px', borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: overviewDays === d.value
                      ? '#111827' : '#fff',
                    color: overviewDays === d.value
                      ? '#fff' : '#374151',
                    fontSize: 11, fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  {d.label}
                </button>
              ))}
            </div>

            {/* KPI cards */}
            {overview && (
              <div style={{
                background: '#3a6ad9', borderRadius: 14,
                padding: '20px 24px', marginBottom: 20,
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 0,
              }}>
                {[
                  {
                    label: 'Total Clicks',
                    value: fmt(overview.current.clicks),
                    delta: overview.deltas.clicks,
                    lowerIsBetter: false,
                  },
                  {
                    label: 'Impressions',
                    value: fmt(overview.current.impressions),
                    delta: overview.deltas.impressions,
                    lowerIsBetter: false,
                  },
                  {
                    label: 'Avg Position',
                    value: fmt(overview.current.avg_position, 1),
                    delta: overview.deltas.avg_position,
                    lowerIsBetter: true,
                    note: 'Lower is better',
                  },
                  {
                    label: 'Avg CTR',
                    value: fmt(overview.current.avg_ctr * 100, 2) + '%',
                    delta: overview.deltas.avg_ctr,
                    lowerIsBetter: false,
                  },
                  {
                    label: 'Keywords w/ Impressions',
                    value: fmt(overview.current.keyword_count),
                    delta: overview.deltas.keyword_count,
                    lowerIsBetter: false,
                  },
                ].map((card, i) => (
                  <div key={card.label} style={{
                    padding: '0 20px',
                    borderRight: i < 4
                      ? '1px solid rgba(255,255,255,0.15)'
                      : 'none',
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700,
                      color: 'rgba(255,255,255,0.6)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em', marginBottom: 8,
                    }}>
                      {card.label}
                    </div>
                    <div style={{
                      fontSize: 26, fontWeight: 800,
                      color: '#fff', fontFamily: 'monospace',
                      marginBottom: 4,
                    }}>
                      {card.value}
                    </div>
                    <DeltaBadge
                      delta={card.delta}
                      lowerIsBetter={card.lowerIsBetter}
                    />
                    {card.note && (
                      <div style={{
                        fontSize: 9, color: 'rgba(255,255,255,0.4)',
                        marginTop: 2,
                      }}>
                        {card.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Position badges */}
            {overview && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12, marginBottom: 20,
              }}>
                {[
                  {
                    label: 'Top 3 Keywords',
                    value: overview.current.top3,
                    color: '#166534', bg: '#dcfce7',
                    note: 'Ranking #1–3',
                  },
                  {
                    label: 'Top 10 Keywords',
                    value: overview.current.top10,
                    color: '#1e40af', bg: '#eff6ff',
                    note: 'Page 1',
                  },
                  {
                    label: 'Total Tracked Keywords',
                    value: keywords.length,
                    color: '#374151', bg: '#f3f4f6',
                    note: 'In Doorly target list',
                  },
                ].map(m => (
                  <div key={m.label} style={{
                    background: m.bg, borderRadius: 12,
                    padding: '16px 20px',
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700,
                      color: m.color, textTransform: 'uppercase',
                      letterSpacing: '0.06em', marginBottom: 6,
                    }}>
                      {m.label}
                    </div>
                    <div style={{
                      fontSize: 32, fontWeight: 800, color: m.color,
                    }}>
                      {m.value}
                    </div>
                    <div style={{ fontSize: 11, color: m.color, opacity: 0.7 }}>
                      {m.note}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Top queries table */}
            {overview?.top_queries?.length > 0 && (
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 12, overflow: 'hidden',
              }}>
                <div style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: 13, fontWeight: 700, color: '#111827',
                }}>
                  Top Queries — Last {overviewDays} Days
                </div>
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {['Query', 'Position', 'Clicks',
                        'Impressions', 'CTR'].map(h => (
                        <th key={h} style={{
                          padding: '8px 16px', textAlign: 'left',
                          fontSize: 10, fontWeight: 700,
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {overview.top_queries.map((q, i) => (
                      <tr key={i} style={{
                        borderTop: '1px solid #f3f4f6',
                      }}>
                        <td style={{
                          padding: '9px 16px', fontSize: 12,
                          color: '#374151', fontWeight: 500,
                        }}>
                          {q.query}
                        </td>
                        <td style={{ padding: '9px 16px' }}>
                          <PositionBadge position={q.position} />
                        </td>
                        <td style={{
                          padding: '9px 16px', fontSize: 12,
                          fontWeight: 700, color: '#111827',
                        }}>
                          {fmt(q.clicks)}
                        </td>
                        <td style={{
                          padding: '9px 16px', fontSize: 12,
                          color: '#6b7280',
                        }}>
                          {fmt(q.impressions)}
                        </td>
                        <td style={{
                          padding: '9px 16px', fontSize: 12,
                          color: '#6b7280',
                        }}>
                          {fmt(q.ctr, 2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!overview && !loading && (
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 10, padding: 32,
                textAlign: 'center', color: '#6b7280', fontSize: 13,
              }}>
                No Search Console data yet — click Sync Rankings to pull data
              </div>
            )}
          </div>
        )}

        {/* ── Keywords Tab ─────────────────────────────────────────── */}
        {!loading && activeTab === 'keywords' && (
          <div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: 10,
              marginBottom: 14,
            }}>
              {/* Crawl Audit — self-crawl of every keyword's target_url
                  whose classification is 'live_no_traffic'. Diagnoses
                  on-page indexability problems (noindex, canonical
                  mismatch, sitemap omission, 4xx/5xx) by FETCHING the
                  pages. Not a third-party audit (no Ahrefs / SEMrush
                  API), just what the page itself reveals over HTTP.
                  Scoped to live_no_traffic specifically — 'recommended'
                  URLs don't exist and 'live' URLs already rank. */}
              <button
                onClick={async () => {
                  setCrawlAuditing(true);
                  setCrawlAuditError(null);
                  try {
                    const r = await axios.post(
                      `${API}/api/admin/seo/run-crawl-audit`,
                      {}, { headers }
                    );
                    setCrawlAuditResult(r.data);
                  } catch (err) {
                    setCrawlAuditError(err.response?.data?.error || err.message);
                    setCrawlAuditResult(null);
                  } finally { setCrawlAuditing(false); }
                }}
                disabled={crawlAuditing}
                style={{
                  background: crawlAuditing ? '#9ca3af' : '#3a6ad9',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '7px 14px', fontSize: 12,
                  fontWeight: 600,
                  cursor: crawlAuditing ? 'default' : 'pointer',
                }}>
                {crawlAuditing ? 'Crawling…' : '🔍 Crawl Audit'}
              </button>
              <button
                onClick={() => setShowAddKeyword(s => !s)}
                style={{
                  background: '#fff', color: '#374151',
                  border: '1px solid #e5e7eb', borderRadius: 8,
                  padding: '7px 14px', fontSize: 12,
                  fontWeight: 600, cursor: 'pointer',
                }}>
                + Add Keyword
              </button>
            </div>

            {/* Crawl Audit results panel — renders below the toolbar
                when an audit has run. Per-URL verdicts color-coded
                (blocked / canonical_issue = red, not_in_sitemap = amber,
                unreachable = gray, healthy_pending = green/neutral).
                Honest framing on healthy_pending so the user doesn't
                read green as 'broken — must fix' when the auditor found
                nothing wrong. */}
            {(crawlAuditError || crawlAuditResult) && (
              <CrawlAuditPanel
                error={crawlAuditError}
                result={crawlAuditResult}
                onDismiss={() => { setCrawlAuditResult(null); setCrawlAuditError(null); }}
              />
            )}

            {showAddKeyword && (
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 12, padding: '18px 20px',
                marginBottom: 16,
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 700,
                  color: '#111827', marginBottom: 12,
                }}>
                  Add Target Keyword
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 10, marginBottom: 10,
                }}>
                  {[
                    { key: 'keyword', label: 'Keyword', type: 'text' },
                    { key: 'target_url', label: 'Target URL', type: 'text' },
                    { key: 'notes', label: 'Notes', type: 'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{
                        fontSize: 10, color: '#6b7280',
                        fontWeight: 600, display: 'block', marginBottom: 3,
                      }}>
                        {f.label}
                      </label>
                      <input
                        type={f.type}
                        value={kwForm[f.key]}
                        onChange={e => setKwForm(fw => ({
                          ...fw, [f.key]: e.target.value
                        }))}
                        style={{
                          width: '100%', padding: '6px 10px',
                          border: '1px solid #e5e7eb', borderRadius: 7,
                          fontSize: 12, boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ))}
                  {[
                    {
                      key: 'intent', label: 'Intent',
                      options: ['transactional','informational',
                        'commercial','navigational'],
                    },
                    {
                      key: 'market', label: 'Market',
                      options: ['both','houston','austin','national'],
                    },
                    {
                      key: 'priority', label: 'Priority',
                      options: ['high','medium','low'],
                    },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{
                        fontSize: 10, color: '#6b7280',
                        fontWeight: 600, display: 'block', marginBottom: 3,
                      }}>
                        {f.label}
                      </label>
                      <select
                        value={kwForm[f.key]}
                        onChange={e => setKwForm(fw => ({
                          ...fw, [f.key]: e.target.value
                        }))}
                        style={{
                          width: '100%', padding: '6px 10px',
                          border: '1px solid #e5e7eb', borderRadius: 7,
                          fontSize: 12, boxSizing: 'border-box',
                        }}>
                        {f.options.map(o => (
                          <option key={o} value={o}>
                            {o.charAt(0).toUpperCase() + o.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <div style={{
                  display: 'flex', gap: 8, justifyContent: 'flex-end',
                }}>
                  <button onClick={() => setShowAddKeyword(false)}
                    style={{
                      background: '#fff', color: '#374151',
                      border: '1px solid #e5e7eb', borderRadius: 7,
                      padding: '6px 14px', fontSize: 12,
                      fontWeight: 600, cursor: 'pointer',
                    }}>
                    Cancel
                  </button>
                  <button
                    disabled={!kwForm.keyword}
                    onClick={async () => {
                      await axios.post(
                        `${API}/api/admin/seo/keywords`,
                        kwForm, { headers }
                      );
                      setShowAddKeyword(false);
                      setKwForm({
                        keyword: '', intent: 'transactional',
                        market: 'both', priority: 'medium',
                        target_url: '', notes: '',
                      });
                      await fetchAll();
                    }}
                    style={{
                      background: !kwForm.keyword ? '#9ca3af' : '#3a6ad9',
                      color: '#fff', border: 'none', borderRadius: 7,
                      padding: '6px 16px', fontSize: 12,
                      fontWeight: 600, cursor: 'pointer',
                    }}>
                    Add Keyword
                  </button>
                </div>
              </div>
            )}

            <div style={{
              background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: 12, overflow: 'hidden',
            }}>
              <table style={{
                width: '100%', borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Keyword', 'Intent', 'Market',
                      'Priority', 'Position', 'Clicks',
                      'Status', 'Target Page', ''].map(h => (
                      <th key={h} style={{
                        padding: '9px 14px', textAlign: 'left',
                        fontSize: 10, fontWeight: 700,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((kw, i) => {
                    const isEditing = editingKwId === kw.id;
                    return (
                    <React.Fragment key={kw.id}>
                    <tr style={{
                      borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                      background: isEditing ? '#f9fafb' : 'transparent',
                    }}>
                      <td style={{
                        padding: '10px 14px', fontSize: 13,
                        fontWeight: 600, color: '#111827',
                      }}>
                        {kw.keyword}
                        {kw.notes && (
                          <div style={{
                            fontSize: 10, color: '#9ca3af',
                            fontWeight: 400, marginTop: 2,
                          }}>
                            {kw.notes}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Pill
                          text={kw.intent}
                          config={INTENT_COLORS[kw.intent]}
                        />
                      </td>
                      <td style={{
                        padding: '10px 14px', fontSize: 12,
                        color: '#374151', textTransform: 'capitalize',
                      }}>
                        {kw.market}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Pill
                          text={kw.priority}
                          config={PRIORITY_COLORS[kw.priority]}
                        />
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <PositionBadge
                          position={kw.latest_ranking?.position}
                        />
                      </td>
                      <td style={{
                        padding: '10px 14px', fontSize: 12,
                        fontWeight: kw.latest_ranking?.clicks > 0
                          ? 700 : 400,
                        color: kw.latest_ranking?.clicks > 0
                          ? '#111827' : '#9ca3af',
                      }}>
                        {kw.latest_ranking?.clicks
                          ? fmt(kw.latest_ranking.clicks)
                          : '—'}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <ClassificationBadge classification={kw.classification} />
                      </td>
                      <td style={{
                        padding: '10px 14px', fontSize: 11,
                        color: '#6b7280', fontFamily: 'monospace',
                      }}>
                        {kw.target_url || '—'}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        {/* Edit + Delete actions per row. Edit toggles
                            an inline edit row below (Fragment-wrapped
                            so the form sits visually under the data
                            row). Delete is a soft-delete — the
                            confirm dialog reflects that the ranking
                            history is preserved. */}
                        <button
                          type="button"
                          title="Edit keyword"
                          onClick={() => {
                            if (isEditing) {
                              setEditingKwId(null);
                            } else {
                              setEditingKwId(kw.id);
                              setEditKwForm({
                                keyword:    kw.keyword || '',
                                intent:     kw.intent || 'transactional',
                                market:     kw.market || 'both',
                                priority:   kw.priority || 'medium',
                                target_url: kw.target_url || '',
                                notes:      kw.notes || '',
                              });
                            }
                          }}
                          style={{
                            background: 'none', border: 'none',
                            cursor: 'pointer', padding: 4,
                            fontSize: 14, color: isEditing ? '#3a6ad9' : '#6b7280',
                          }}>
                          ✎
                        </button>
                        <button
                          type="button"
                          title="Deactivate keyword"
                          onClick={async () => {
                            if (!window.confirm(`Deactivate "${kw.keyword}"?\n\nRanking history is preserved — this hides the keyword from the active list but doesn't delete the underlying seo_rankings snapshots.`)) return;
                            try {
                              await axios.delete(`${API}/api/admin/seo/keywords/${kw.id}`, { headers });
                              await fetchAll();
                            } catch (err) {
                              alert(err.response?.data?.error || err.message);
                            }
                          }}
                          style={{
                            background: 'none', border: 'none',
                            cursor: 'pointer', padding: 4,
                            fontSize: 14, color: '#9ca3af', marginLeft: 4,
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = '#ff082f'}
                          onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
                          🗑
                        </button>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr style={{ background: '#f9fafb', borderTop: 'none' }}>
                        <td colSpan={9} style={{ padding: '12px 14px 16px' }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 10, marginBottom: 10,
                          }}>
                            <label style={{ fontSize: 11, color: '#6b7280' }}>
                              Keyword
                              <input
                                type="text"
                                value={editKwForm.keyword}
                                onChange={e => setEditKwForm(f => ({ ...f, keyword: e.target.value }))}
                                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ fontSize: 11, color: '#6b7280' }}>
                              Intent
                              <select
                                value={editKwForm.intent}
                                onChange={e => setEditKwForm(f => ({ ...f, intent: e.target.value }))}
                                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}>
                                <option value="transactional">transactional</option>
                                <option value="informational">informational</option>
                                <option value="navigational">navigational</option>
                                <option value="commercial">commercial</option>
                              </select>
                            </label>
                            <label style={{ fontSize: 11, color: '#6b7280' }}>
                              Market
                              <select
                                value={editKwForm.market}
                                onChange={e => setEditKwForm(f => ({ ...f, market: e.target.value }))}
                                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}>
                                <option value="both">both</option>
                                <option value="houston">houston</option>
                                <option value="austin">austin</option>
                                <option value="national">national</option>
                              </select>
                            </label>
                            <label style={{ fontSize: 11, color: '#6b7280' }}>
                              Priority
                              <select
                                value={editKwForm.priority}
                                onChange={e => setEditKwForm(f => ({ ...f, priority: e.target.value }))}
                                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}>
                                <option value="high">high</option>
                                <option value="medium">medium</option>
                                <option value="low">low</option>
                              </select>
                            </label>
                            <label style={{ fontSize: 11, color: '#6b7280', gridColumn: 'span 2' }}>
                              Target URL
                              <input
                                type="text"
                                value={editKwForm.target_url}
                                onChange={e => setEditKwForm(f => ({ ...f, target_url: e.target.value }))}
                                placeholder="https://godoorly.com/..."
                                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ fontSize: 11, color: '#6b7280', gridColumn: 'span 3' }}>
                              Notes
                              <input
                                type="text"
                                value={editKwForm.notes}
                                onChange={e => setEditKwForm(f => ({ ...f, notes: e.target.value }))}
                                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
                            </label>
                          </div>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => setEditingKwId(null)}
                              style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={!editKwForm.keyword}
                              onClick={async () => {
                                try {
                                  await axios.patch(`${API}/api/admin/seo/keywords/${kw.id}`, editKwForm, { headers });
                                  setEditingKwId(null);
                                  await fetchAll();
                                } catch (err) {
                                  alert(err.response?.data?.error || err.message);
                                }
                              }}
                              style={{
                                background: !editKwForm.keyword ? '#9ca3af' : '#3a6ad9',
                                color: '#fff', border: 'none', borderRadius: 7,
                                padding: '6px 16px', fontSize: 12, fontWeight: 600,
                                cursor: !editKwForm.keyword ? 'default' : 'pointer',
                              }}>
                              Save
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );})}
                </tbody>
              </table>
              {keywords.length === 0 && (
                <div style={{
                  padding: 24, textAlign: 'center',
                  color: '#9ca3af', fontSize: 12,
                }}>
                  No keywords yet
                </div>
              )}
            </div>

            {/* ── Webflow Blog Posts ────────────────────────────────────
                Lists the published items in the configured Webflow
                blog collection. Doesn't auto-track them yet — the
                strategist registers any blog URL as a keyword target
                via the Add Keyword button using the URL shown here.
                Fail-soft: when Webflow is unavailable or disabled,
                renders an amber banner instead of the list. */}
            <div style={{ marginTop: 28 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline',
                justifyContent: 'space-between', marginBottom: 12,
              }}>
                <h2 style={{
                  margin: 0, fontSize: 16, fontWeight: 700,
                  color: '#111827',
                }}>
                  Blog Posts <span style={{ color: '#9ca3af', fontWeight: 500, fontSize: 13 }}>
                    ({blogPosts.posts?.length || 0} published)
                  </span>
                </h2>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  Sourced from Webflow collection · register as keyword targets to track
                </div>
              </div>
              {blogPosts.available === false ? (
                <div style={{
                  padding: '12px 14px',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: 7,
                  fontSize: 12, color: '#92400e',
                }}>
                  <strong>Webflow unavailable.</strong> Classification will show
                  "Unknown" until the API recovers. Reason:{' '}
                  {blogPosts.reason || 'unspecified'}
                </div>
              ) : blogPosts.posts.length === 0 ? (
                <div style={{
                  padding: 18, background: '#f9fafb',
                  border: '1px solid #e5e7eb', borderRadius: 8,
                  textAlign: 'center', fontSize: 12, color: '#9ca3af',
                }}>
                  No published blog posts in the configured collection.
                </div>
              ) : (
                <div style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10, overflow: 'hidden',
                }}>
                  {blogPosts.posts.map((post, i) => (
                    <div key={post.url || post.slug || i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 600,
                          color: '#111827',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {post.name || post.slug || '(untitled)'}
                        </div>
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 11, color: '#3a6ad9',
                            fontFamily: 'monospace',
                            textDecoration: 'none',
                          }}>
                          {post.url}
                        </a>
                      </div>
                      {post.lastPublished && (
                        <div style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {new Date(post.lastPublished).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Discovery Tab ────────────────────────────────────────── */}
        {!loading && activeTab === 'discovery' && (
          <DiscoveryPanel
            seeds={discoverySeeds}
            setSeeds={setDiscoverySeeds}
            result={discoverResult}
            error={discoverError}
            running={discoverRunning}
            onDiscover={async () => {
              setDiscoverRunning(true);
              setDiscoverError(null);
              try {
                const r = await axios.post(
                  `${API}/api/admin/seo/discover-keywords`,
                  { seeds: discoverySeeds },
                  { headers, timeout: 90_000 } // ~30s per seed worst-case, parallelized
                );
                setDiscoverResult(r.data);
              } catch (err) {
                setDiscoverError(err.response?.data?.error || err.message);
              } finally {
                setDiscoverRunning(false);
              }
            }}
            targetOptions={targetOptions}
            onLoadTargetOptions={async () => {
              try {
                const r = await axios.get(`${API}/api/admin/seo/discover-target-options`, { headers });
                setTargetOptions(Array.isArray(r.data?.options) ? r.data.options : []);
              } catch (err) {
                // On failure, set empty array — the form will render a
                // free-text fallback (with the honest warning the
                // discovery flow exists specifically to avoid).
                console.error('[Discovery] target-options load failed:', err.message);
                setTargetOptions([]);
              }
            }}
            addingCandidate={addingCandidate}
            setAddingCandidate={setAddingCandidate}
            addForm={addForm}
            setAddForm={setAddForm}
            onAddKeyword={async (candidate) => {
              try {
                await axios.post(
                  `${API}/api/admin/seo/keywords`,
                  {
                    keyword:    candidate.query,
                    target_url: addForm.target_url || null,
                    intent:     addForm.intent,
                    market:     addForm.market,
                    priority:   addForm.priority,
                    notes:      addForm.notes
                      || `Discovered via Serper ${candidate.source.toUpperCase()} — seed: "${candidate.found_via_seed}"`,
                  },
                  { headers }
                );
                // Mark this candidate as already_tracked in place so
                // the row re-renders into the greyed state. Avoids a
                // full discovery re-run just to flip one flag.
                setDiscoverResult(prev => prev ? {
                  ...prev,
                  candidates: prev.candidates.map(c =>
                    c.query.toLowerCase() === candidate.query.toLowerCase()
                      ? { ...c, already_tracked: true }
                      : c
                  ),
                  new_count: Math.max(0, prev.new_count - 1),
                } : prev);
                setAddingCandidate(null);
                // Refresh the keyword list so the Keywords tab count is
                // accurate when the user switches tabs.
                try {
                  const kw = await axios.get(`${API}/api/admin/seo/keywords`, { headers });
                  setKeywords(Array.isArray(kw.data) ? kw.data : []);
                } catch { /* count stays slightly stale until next fetchAll */ }
              } catch (err) {
                alert('Add failed: ' + (err.response?.data?.error || err.message));
              }
            }}
          />
        )}

        {/* ── Scoreboard Tab ───────────────────────────────────────── */}
        {!loading && activeTab === 'scoreboard' && (
          <ScoreboardPanel
            data={scoreboard}
            loading={scoreboardLoading}
            error={scoreboardError}
            onRefresh={async (bust) => {
              setScoreboardLoading(true);
              setScoreboardError(null);
              try {
                const r = await axios.get(
                  `${API}/api/admin/seo/brand-scoreboard${bust ? '?refresh=1' : ''}`,
                  { headers }
                );
                setScoreboard(r.data);
              } catch (err) {
                setScoreboardError(err.response?.data?.error || err.message);
              } finally {
                setScoreboardLoading(false);
              }
            }}
          />
        )}

        {/* ── Interlinks Tab ───────────────────────────────────────── */}
        {!loading && activeTab === 'interlinks' && (
          <InterlinksPanel
            data={interlinks}
            loading={interlinksLoading}
            error={interlinksError}
            scanning={interlinksScanning}
            onLoad={async () => {
              setInterlinksLoading(true);
              setInterlinksError(null);
              try {
                const r = await axios.get(`${API}/api/admin/seo/interlinks`, { headers });
                setInterlinks(r.data);
              } catch (err) {
                setInterlinksError(err.response?.data?.error || err.message);
              } finally {
                setInterlinksLoading(false);
              }
            }}
            onScan={async () => {
              setInterlinksScanning(true);
              try {
                await axios.post(`${API}/api/admin/seo/scan-interlinks`, {}, { headers });
                // Reload the matrix after scan completes
                const r = await axios.get(`${API}/api/admin/seo/interlinks`, { headers });
                setInterlinks(r.data);
              } catch (err) {
                setInterlinksError(err.response?.data?.error || err.message);
              } finally {
                setInterlinksScanning(false);
              }
            }}
            onManualToggle={async (fromId, toId, linkExists) => {
              try {
                await axios.patch(
                  `${API}/api/admin/seo/interlinks/manual`,
                  { from_property_id: fromId, to_property_id: toId, link_exists: linkExists },
                  { headers }
                );
                const r = await axios.get(`${API}/api/admin/seo/interlinks`, { headers });
                setInterlinks(r.data);
              } catch (err) {
                alert(err.response?.data?.error || err.message);
              }
            }}
          />
        )}

        {/* ── Technical Audit Tab ──────────────────────────────────── */}
        {!loading && activeTab === 'technical' && (
          <TechnicalAuditPanel
            data={technicalAudit}
            loading={technicalAuditLoading}
            error={technicalAuditError}
            running={technicalAuditRunning}
            onLoad={async () => {
              setTechnicalAuditLoading(true);
              setTechnicalAuditError(null);
              try {
                const r = await axios.get(`${API}/api/admin/seo/technical-audit`, { headers });
                setTechnicalAudit(r.data);
              } catch (err) {
                setTechnicalAuditError(err.response?.data?.error || err.message);
              } finally {
                setTechnicalAuditLoading(false);
              }
            }}
            onRun={async () => {
              setTechnicalAuditRunning(true);
              setTechnicalAuditError(null);
              try {
                // PSI is slow — ~30s per page × 6 pages = ~3 min wall time.
                // Bump the axios timeout for this one call so it doesn't
                // abort while the backend is still working through the
                // serial-per-page loop. Reasonable upper bound is 5 min;
                // anything longer is a real upstream failure.
                const r = await axios.post(
                  `${API}/api/admin/seo/run-technical-audit`,
                  {},
                  { headers, timeout: 5 * 60 * 1000 }
                );
                setTechnicalAudit(r.data);
              } catch (err) {
                setTechnicalAuditError(err.response?.data?.error || err.message);
              } finally {
                setTechnicalAuditRunning(false);
              }
            }}
          />
        )}

        {/* ── Backlinks Tab ────────────────────────────────────────── */}
        {!loading && activeTab === 'backlinks' && (
          <div>
            {/* Summary */}
            {backlinks?.summary && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 12, marginBottom: 20,
              }}>
                {[
                  { label: 'Total Targets',   value: backlinks.summary.total,      color: '#374151', bg: '#f3f4f6' },
                  { label: 'Linked',          value: backlinks.summary.linked,      color: '#166534', bg: '#dcfce7' },
                  { label: 'Pitched',         value: backlinks.summary.pitched,     color: '#1e40af', bg: '#eff6ff' },
                  { label: 'Identified',      value: backlinks.summary.identified,  color: '#854d0e', bg: '#fef9c3' },
                ].map(m => (
                  <div key={m.label} style={{
                    background: m.bg, borderRadius: 10,
                    padding: '14px 16px',
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700,
                      color: m.color, textTransform: 'uppercase',
                      letterSpacing: '0.06em', marginBottom: 4,
                    }}>
                      {m.label}
                    </div>
                    <div style={{
                      fontSize: 24, fontWeight: 800, color: m.color,
                    }}>
                      {m.value}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'flex-end',
              marginBottom: 12,
            }}>
              <button
                onClick={() => setShowAddBacklink(s => !s)}
                style={{
                  background: '#fff', color: '#374151',
                  border: '1px solid #e5e7eb', borderRadius: 8,
                  padding: '7px 14px', fontSize: 12,
                  fontWeight: 600, cursor: 'pointer',
                }}>
                + Add Target
              </button>
            </div>

            {showAddBacklink && (
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 12, padding: '18px 20px',
                marginBottom: 16,
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 10, marginBottom: 10,
                }}>
                  {[
                    { key: 'domain', label: 'Domain', type: 'text' },
                    { key: 'page_url', label: 'Target Page URL', type: 'text' },
                    { key: 'domain_authority', label: 'Domain Authority (0-100)', type: 'number' },
                    { key: 'notes', label: 'Notes', type: 'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{
                        fontSize: 10, color: '#6b7280',
                        fontWeight: 600, display: 'block', marginBottom: 3,
                      }}>
                        {f.label}
                      </label>
                      <input
                        type={f.type}
                        value={blForm[f.key]}
                        onChange={e => setBlForm(bf => ({
                          ...bf, [f.key]: e.target.value
                        }))}
                        style={{
                          width: '100%', padding: '6px 10px',
                          border: '1px solid #e5e7eb', borderRadius: 7,
                          fontSize: 12, boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ))}
                  <div>
                    <label style={{
                      fontSize: 10, color: '#6b7280',
                      fontWeight: 600, display: 'block', marginBottom: 3,
                    }}>
                      Relevance
                    </label>
                    <select
                      value={blForm.relevance}
                      onChange={e => setBlForm(bf => ({
                        ...bf, relevance: e.target.value
                      }))}
                      style={{
                        width: '100%', padding: '6px 10px',
                        border: '1px solid #e5e7eb', borderRadius: 7,
                        fontSize: 12, boxSizing: 'border-box',
                      }}>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
                <div style={{
                  display: 'flex', gap: 8, justifyContent: 'flex-end',
                }}>
                  <button onClick={() => setShowAddBacklink(false)}
                    style={{
                      background: '#fff', color: '#374151',
                      border: '1px solid #e5e7eb', borderRadius: 7,
                      padding: '6px 14px', fontSize: 12,
                      fontWeight: 600, cursor: 'pointer',
                    }}>
                    Cancel
                  </button>
                  <button
                    disabled={!blForm.domain}
                    onClick={async () => {
                      await axios.post(
                        `${API}/api/admin/seo/backlinks`,
                        blForm, { headers }
                      );
                      setShowAddBacklink(false);
                      setBlForm({
                        domain: '', page_url: '',
                        relevance: 'medium',
                        domain_authority: '', notes: '',
                      });
                      await fetchAll();
                    }}
                    style={{
                      background: !blForm.domain ? '#9ca3af' : '#3a6ad9',
                      color: '#fff', border: 'none', borderRadius: 7,
                      padding: '6px 16px', fontSize: 12,
                      fontWeight: 600, cursor: 'pointer',
                    }}>
                    Add Target
                  </button>
                </div>
              </div>
            )}

            {/* Backlink targets list */}
            <div style={{
              background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: 12, overflow: 'hidden',
            }}>
              <table style={{
                width: '100%', borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Domain', 'DA', 'Relevance', 'Source',
                      'Status', ''].map(h => (
                      <th key={h} style={{
                        padding: '9px 14px', textAlign: 'left',
                        fontSize: 10, fontWeight: 700,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(backlinks?.targets || []).map((t, i) => {
                    const sc = BACKLINK_STATUS_COLORS[t.status]
                      || BACKLINK_STATUS_COLORS.identified;
                    return (
                      <tr key={t.id} style={{
                        borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                      }}>
                        <td style={{
                          padding: '10px 14px',
                        }}>
                          <div style={{
                            fontSize: 13, fontWeight: 600,
                            color: '#111827',
                          }}>
                            {t.domain}
                          </div>
                          {t.notes && (
                            <div style={{
                              fontSize: 10, color: '#9ca3af', marginTop: 2,
                            }}>
                              {t.notes}
                            </div>
                          )}
                          {t.pitch_subject && (
                            <div style={{
                              fontSize: 10, color: '#3a6ad9', marginTop: 2,
                            }}>
                              PR: {t.pitch_subject}
                            </div>
                          )}
                        </td>
                        <td style={{
                          padding: '10px 14px', fontSize: 12,
                          color: '#374151', fontWeight: 600,
                        }}>
                          {t.domain_authority || '—'}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <Pill
                            text={t.relevance}
                            config={PRIORITY_COLORS[t.relevance]}
                          />
                        </td>
                        <td style={{
                          padding: '10px 14px', fontSize: 11,
                          color: t.source === 'pr_agent'
                            ? '#1e40af' : '#6b7280',
                        }}>
                          {t.source === 'pr_agent' ? 'PR Agent' : 'Manual'}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <select
                            value={t.status}
                            onChange={async e => {
                              await axios.patch(
                                `${API}/api/admin/seo/backlinks/${t.id}`,
                                {
                                  status: e.target.value,
                                  linked_at: e.target.value === 'linked'
                                    ? new Date().toISOString() : undefined,
                                },
                                { headers }
                              );
                              await fetchAll();
                            }}
                            style={{
                              fontSize: 11, fontWeight: 600,
                              background: sc.bg, color: sc.color,
                              border: 'none', borderRadius: 6,
                              padding: '3px 8px', cursor: 'pointer',
                            }}>
                            {Object.entries(BACKLINK_STATUS_COLORS)
                              .map(([val, cfg]) => (
                                <option key={val} value={val}>
                                  {cfg.label}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {t.linked_at && (
                            <span style={{
                              fontSize: 10, color: '#166534',
                              fontWeight: 600,
                            }}>
                              ✓ {new Date(t.linked_at)
                                .toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric',
                                  year: 'numeric',
                                })}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!backlinks?.targets || backlinks.targets.length === 0) && (
                <div style={{
                  padding: 24, textAlign: 'center',
                  color: '#9ca3af', fontSize: 12,
                }}>
                  No backlink targets yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Audits Tab removed — was a historical table of aggregate
            GSC clicks/impressions/avg-position/CTR/keywords-top-N
            already surfaced on the Overview tab. Crawl Audit (POST
            /run-crawl-audit, surfaced via Keywords tab) and Technical
            Audit (POST /run-technical-audit, dedicated tab) are
            distinct and retained. */}

        {/* ── Recommendations Tab ──────────────────────────────────── */}
        {!loading && activeTab === 'recommendations' && (
          <div>
            {recs.length === 0 ? (
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 10, padding: 32,
                textAlign: 'center', color: '#6b7280', fontSize: 13,
              }}>
                No recommendations yet — click Generate Recommendations
                to analyze your SEO data
              </div>
            ) : (
              recs.map(rec => {
                const tc = REC_TYPE_COLORS[rec.recommendation_type]
                  || REC_TYPE_COLORS.content_gap;
                const pc = PRIORITY_COLORS[rec.priority]
                  || PRIORITY_COLORS.medium;
                const STATUS_COLORS_REC = {
                  pending_review: { bg: '#fef9c3', color: '#854d0e' },
                  approved:       { bg: '#eff6ff', color: '#1e40af' },
                  rejected:       { bg: '#f3f4f6', color: '#6b7280' },
                  in_progress:    { bg: '#f3e8ff', color: '#6b21a8' },
                  completed:      { bg: '#dcfce7', color: '#166534' },
                };
                const sc = STATUS_COLORS_REC[rec.status]
                  || STATUS_COLORS_REC.pending_review;

                return (
                  <div key={rec.id} style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: '16px 20px',
                    marginBottom: 10,
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 10,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex', gap: 6,
                          alignItems: 'center', marginBottom: 6,
                          flexWrap: 'wrap',
                        }}>
                          <Pill text={tc.label} config={tc} />
                          <Pill text={rec.priority} config={pc} />
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            background: sc.bg, color: sc.color,
                            borderRadius: 999, padding: '2px 8px',
                          }}>
                            {rec.status.replace('_', ' ')
                              .replace(/^\w/, c => c.toUpperCase())}
                          </span>
                        </div>
                        <div style={{
                          fontSize: 14, fontWeight: 700,
                          color: '#111827', marginBottom: 4,
                        }}>
                          {rec.title}
                        </div>
                        <div style={{
                          fontSize: 12, color: '#374151',
                          lineHeight: 1.5, marginBottom: 8,
                        }}>
                          {rec.description}
                        </div>
                        <div style={{
                          display: 'flex', gap: 16,
                          fontSize: 11, color: '#6b7280',
                          flexWrap: 'wrap',
                        }}>
                          {rec.affected_keyword && (
                            <span>
                              Keyword: <strong style={{ color: '#374151' }}>
                                {rec.affected_keyword}
                              </strong>
                            </span>
                          )}
                          {rec.current_value && (
                            <span>
                              Current: <strong style={{ color: '#374151' }}>
                                {rec.current_value}
                              </strong>
                            </span>
                          )}
                          {rec.target_value && (
                            <span>
                              Target: <strong style={{ color: '#166534' }}>
                                {rec.target_value}
                              </strong>
                            </span>
                          )}
                          {rec.estimated_impact && (
                            <span style={{ color: '#3a6ad9', fontWeight: 600 }}>
                              Impact: {rec.estimated_impact}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {rec.status === 'pending_review' && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={async () => {
                            await axios.patch(
                              `${API}/api/admin/seo/recommendations/${rec.id}`,
                              { status: 'approved' }, { headers }
                            );
                            await fetchAll();
                          }}
                          style={{
                            background: '#3a6ad9', color: '#fff',
                            border: 'none', borderRadius: 7,
                            padding: '6px 14px', fontSize: 11,
                            fontWeight: 600, cursor: 'pointer',
                          }}>
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            const reason = window.prompt('Reason (optional):');
                            if (reason !== null) {
                              await axios.patch(
                                `${API}/api/admin/seo/recommendations/${rec.id}`,
                                { status: 'rejected', rejected_reason: reason },
                                { headers }
                              );
                              await fetchAll();
                            }
                          }}
                          style={{
                            background: '#fff', color: '#374151',
                            border: '1px solid #e5e7eb', borderRadius: 7,
                            padding: '6px 14px', fontSize: 11,
                            fontWeight: 600, cursor: 'pointer',
                          }}>
                          Reject
                        </button>
                        <button
                          onClick={async () => {
                            await axios.patch(
                              `${API}/api/admin/seo/recommendations/${rec.id}`,
                              { status: 'in_progress' }, { headers }
                            );
                            await fetchAll();
                          }}
                          style={{
                            background: '#f3e8ff', color: '#6b21a8',
                            border: 'none', borderRadius: 7,
                            padding: '6px 14px', fontSize: 11,
                            fontWeight: 600, cursor: 'pointer',
                          }}>
                          Mark In Progress
                        </button>
                      </div>
                    )}

                    {rec.status === 'approved' && (
                      <button
                        onClick={async () => {
                          await axios.patch(
                            `${API}/api/admin/seo/recommendations/${rec.id}`,
                            { status: 'completed' }, { headers }
                          );
                          await fetchAll();
                        }}
                        style={{
                          background: '#acf44e', color: '#111827',
                          border: 'none', borderRadius: 7,
                          padding: '6px 14px', fontSize: 11,
                          fontWeight: 700, cursor: 'pointer',
                        }}>
                        Mark Complete
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
